import { useEffect, useState, type ReactNode } from "react";
import clsx from "clsx";
import { Spinner } from "@/components/ui/Spinner";
import {
  getMaAttachmentThumbUrl,
  getMaAttachmentUrl,
  getMaterialAnforderung,
  type MaDetail,
} from "@/lib/material";
import { Lightbox } from "@/pages/material/Lightbox";
import { useMaMediaContextMenu } from "@/pages/material/useMaMediaContextMenu";
import { imageElementFromContextEvent } from "@/lib/clipboardMedia";
import {
  fmtMaDateTime,
  MaFortschrittBadge,
  maLieferterminSummary,
  MaPositionOrderBadge,
  formatMaPositionOrderSummary,
  maProjectLine,
} from "@/pages/material/maListDisplay";

export interface MaContextPanelProps {
  maId: number;
  className?: string;
}

export function MaWizardSplitLayout({
  sourceMaId,
  children,
}: {
  sourceMaId?: number | null;
  children: ReactNode;
}) {
  if (!sourceMaId) return <>{children}</>;

  return (
    <div className="ma-wizard-split flex min-h-0 flex-1 flex-col gap-4 lg:flex-row lg:min-h-0 lg:items-stretch">
      <aside
        className="ma-wizard-split__aside shrink-0 overflow-hidden rounded-ios border border-separator bg-card-elevated lg:flex lg:min-h-0 lg:w-[min(360px,38%)] lg:flex-col"
      >
        <MaContextPanel
          maId={sourceMaId}
          className="max-h-[min(42vh,420px)] lg:max-h-none lg:min-h-0 lg:flex-1"
        />
      </aside>
      <div className="ma-wizard-split__main min-h-0 min-w-0 flex-1 lg:overflow-y-auto lg:material-mobile-scroll">
        {children}
      </div>
    </div>
  );
}

export function MaContextPanel({ maId, className }: MaContextPanelProps) {
  const [detail, setDetail] = useState<MaDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [lightboxCopySource, setLightboxCopySource] = useState<{
    url: string;
    filename: string;
    contentType: string;
  } | null>(null);
  const { openMediaContextMenu, contextMenuNode } = useMaMediaContextMenu();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void getMaterialAnforderung(maId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch(() => {
        if (!cancelled) {
          setDetail(null);
          setError("Materialanforderung konnte nicht geladen werden.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [maId]);

  if (loading) {
    return (
      <div
        className={clsx(
          "flex items-center justify-center gap-2 px-3 py-8 text-sm text-label-secondary",
          className,
        )}
      >
        <Spinner />
        <span>MA wird geladen …</span>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <p className={clsx("px-3 py-6 text-sm text-label-secondary", className)}>{error || "—"}</p>
    );
  }

  const projectLine = maProjectLine(detail);
  const storedAttachments = (detail.attachments || []).filter((a) => a.position_id == null);

  return (
    <>
      <div
        className={clsx(
          "flex h-full flex-col overflow-y-auto overflow-x-hidden px-3 py-3 material-mobile-scroll",
          className,
        )}
      >
        <header className="shrink-0 border-b border-separator pb-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-label-secondary">
            Materialanforderung
          </p>
          <p className="mt-1 font-mono text-sm font-semibold text-label">{detail.ma_nummer}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <MaFortschrittBadge f={detail.monteur_fortschritt} status={detail.status} />
            {maLieferterminSummary(detail) ? (
              <span className="text-xs text-label-secondary">
                LT {maLieferterminSummary(detail)}
              </span>
            ) : null}
          </div>
          {projectLine ? (
            <p className="mt-2 text-sm font-medium leading-snug text-label">{projectLine}</p>
          ) : null}
          <p className="mt-1 text-xs text-label-secondary">
            {detail.created_by} · {fmtMaDateTime(detail.created_at)}
          </p>
        </header>

        {detail.notes ? (
          <section className="mt-3 shrink-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-label-secondary">
              Nachricht Monteur
            </p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-label">{detail.notes}</p>
          </section>
        ) : null}

        {detail.buero_notizen ? (
          <section className="mt-3 shrink-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-label-secondary">
              Büro-Notizen
            </p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-label-secondary">
              {detail.buero_notizen}
            </p>
          </section>
        ) : null}

        <section className="mt-3 shrink-0">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-label-secondary">
              Positionen ({detail.positionen?.length ?? 0})
            </p>
            {detail.positionen?.length ? (
              <p className="text-[11px] text-label-tertiary">
                {formatMaPositionOrderSummary(detail.positionen) || "—"}
              </p>
            ) : null}
          </div>
          {detail.positionen?.length ? (
            <ul className="mt-2 min-w-0 divide-y divide-separator text-sm">
              {detail.positionen.map((p, idx) => (
                <li key={p.id ?? idx} className="min-w-0 py-2">
                  <div className="flex min-w-0 items-start justify-between gap-2">
                    <p className="min-w-0 flex-1 break-words text-label [overflow-wrap:anywhere]">
                      <span className="font-medium tabular-nums">
                        {p.menge != null ? p.menge : "—"}
                      </span>
                      {p.einheit ? <span className="mx-1">{p.einheit}</span> : null}
                      <span>{p.text}</span>
                    </p>
                    <span className="shrink-0">
                      <MaPositionOrderBadge position={p} />
                    </span>
                  </div>
                  {p.notes ? (
                    <p className="mt-0.5 whitespace-pre-wrap break-words text-xs text-label-tertiary [overflow-wrap:anywhere]">
                      {p.notes}
                    </p>
                  ) : null}
                  {p.hero_nr ? (
                    <p className="mt-0.5 text-xs text-accent">Hero {p.hero_nr}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-1 text-sm text-label-tertiary">Keine Positionen.</p>
          )}
        </section>

        {storedAttachments.length > 0 ? (
          <section className="mt-3 shrink-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-label-secondary">
              Anhänge ({storedAttachments.length})
            </p>
            <ul className="mt-2 grid grid-cols-2 gap-2">
              {storedAttachments.map((a) => {
                const isImg = (a.content_type || "").startsWith("image/");
                const fullUrl = getMaAttachmentUrl(detail.id, a.id);
                const thumbUrl = getMaAttachmentThumbUrl(detail.id, a.id);
                return (
                  <li
                    key={a.id}
                    className="overflow-hidden rounded-lg border border-separator bg-card"
                  >
                    {isImg ? (
                      <button
                        type="button"
                        onClick={() => {
                          setLightboxUrl(thumbUrl);
                          setLightboxCopySource({
                            url: thumbUrl,
                            filename: a.filename,
                            contentType: a.content_type,
                          });
                        }}
                        onContextMenu={(e) =>
                          openMediaContextMenu(e, {
                            url: fullUrl,
                            filename: a.filename,
                            contentType: a.content_type,
                            isImage: true,
                            imageElement: imageElementFromContextEvent(e.target, e.currentTarget),
                          })
                        }
                        className="block h-24 w-full overflow-hidden bg-fill-tertiary"
                      >
                        <img
                          src={thumbUrl}
                          alt={a.filename}
                          loading="lazy"
                          crossOrigin="use-credentials"
                          className="h-full w-full object-cover"
                        />
                      </button>
                    ) : (
                      <a
                        href={fullUrl}
                        target="_blank"
                        rel="noopener"
                        onContextMenu={(e) =>
                          openMediaContextMenu(e, {
                            url: fullUrl,
                            filename: a.filename,
                            contentType: a.content_type,
                            isImage: false,
                          })
                        }
                        className="block px-2 py-4 text-center text-xs text-label hover:bg-fill-tertiary"
                      >
                        {a.filename}
                      </a>
                    )}
                    <p className="truncate px-2 py-1 text-[10px] text-label-tertiary">{a.filename}</p>
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}
      </div>

      <Lightbox
        url={lightboxUrl}
        copySource={lightboxCopySource}
        onClose={() => {
          setLightboxUrl(null);
          setLightboxCopySource(null);
        }}
        zIndexClass="z-[130]"
      />
      {contextMenuNode}
    </>
  );
}
